import { useSelector, useDispatch } from "react-redux";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { setChannel } from "../features/channelSlice";
import { playVibratingSound, stopVibratingSound } from '../utils/soundUtils';
import { shouldUseMobileUI } from '../utils/deviceUtils';
import tokenManager from '../utils/tokenManager';
import ChannelContent from "./ChannelContent";
import ChannelSnapshot from "./ChannelSnapshot";
import NotificationContainer from "./NotificationContainer"; // Import NotificationContainer
import HomeView from "./HomeView";
import { VoiceCallView } from "./call";

// Helper function to create bezel gradient from base color with more depth and metallic feel
const createBezelGradient = (baseColor) => {
  // Handle undefined/null values with default dark color to match iPhone style
  if (!baseColor || typeof baseColor !== 'string') {
    baseColor = '#1a1a2e'; // Default dark iPhone-like color (rgb(26, 26, 46))
  }
  
  // Convert hex to RGB for calculations
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // For dark colors (like #1a1a2e), create a subtle metallic gradient
  // For lighter colors, use more dramatic variations
  const isDark = (r + g + b) / 3 < 100;
  
  if (isDark) {
    // Very subtle variations for dark bezels to match the HTML example
    // Example shows: rgb(66,66,96) -> rgb(46,46,71) -> rgb(26,26,46) -> rgb(18,18,34) -> rgb(14,14,28)
    // This means variations of roughly +40 to -12 from the base color
    const highlight = `rgb(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 50)})`; // Top highlight
    const lightMid = `rgb(${Math.min(255, r + 20)}, ${Math.min(255, g + 20)}, ${Math.min(255, b + 25)})`; // Light transition
    const mid = `rgb(${r}, ${g}, ${b})`; // Base color (center)
    const darkMid = `rgb(${Math.max(0, r - 8)}, ${Math.max(0, g - 8)}, ${Math.max(0, b - 12)})`; // Dark transition
    const shadow = `rgb(${Math.max(0, r - 12)}, ${Math.max(0, g - 12)}, ${Math.max(0, b - 18)})`; // Bottom shadow
    
    return `linear-gradient(145deg, 
      ${highlight} 0%, 
      ${lightMid} 12%, 
      ${mid} 25%, 
      ${mid} 50%, 
      ${darkMid} 75%, 
      ${shadow} 92%, 
      ${shadow} 100%)`;
  } else {
    // More dramatic gradient for lighter bezel colors (silver, etc.)
    const highlight = `rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)})`; 
    const lightMid = `rgb(${Math.min(255, r + 25)}, ${Math.min(255, g + 25)}, ${Math.min(255, b + 25)})`; 
    const mid = `rgb(${r}, ${g}, ${b})`; 
    const darkMid = `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`; 
    const shadow = `rgb(${Math.max(0, r - 60)}, ${Math.max(0, g - 60)}, ${Math.max(0, b - 60)})`; 
    
    return `linear-gradient(145deg, 
      ${highlight} 0%, 
      ${lightMid} 15%, 
      ${mid} 30%, 
      ${mid} 50%, 
      ${darkMid} 70%, 
      ${shadow} 90%, 
      ${shadow} 100%)`;
  }
};

// Helper function to create home button gradient (darker version of bezel color)
const createHomeButtonGradient = (baseColor) => {
  // Handle undefined/null values with default dark color
  if (!baseColor || typeof baseColor !== 'string') {
    baseColor = '#1a1a2e'; // Default dark iPhone-like color
  }
  
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Create darker variations for home button
  const dark1 = `rgb(${Math.max(0, r - 80)}, ${Math.max(0, g - 80)}, ${Math.max(0, b - 80)})`;
  const dark2 = `rgb(${Math.max(0, r - 100)}, ${Math.max(0, g - 100)}, ${Math.max(0, b - 100)})`;
  const dark3 = `rgb(${Math.max(0, r - 120)}, ${Math.max(0, g - 120)}, ${Math.max(0, b - 120)})`;
  
  return `linear-gradient(135deg, ${dark1} 0%, ${dark2} 50%, ${dark3} 100%)`;
};

// Device style config for phone/tablet
const getDeviceStyles = (bezelColor = '#c7c7cc') => ({
  phone: {
    className: "device-bezel is-iphone flex flex-col items-center justify-center",
    style: {
      width: '420px',
      height: '850px',
      borderRadius: 60,
      padding: '8px',
      boxSizing: 'border-box',
      boxShadow: '0 25px 50px -15px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.15), inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.1)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: createBezelGradient(bezelColor),
      border: '1px solid rgba(255,255,255,0.8)',
      transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
    },
    screenClass: "device-screen bg-black w-full h-full flex flex-col rounded-[48px] select-none text-[11px] relative overflow-hidden",
    screenStyle: {
      position: 'relative',
      width: '100%',
      height: '100%',
      zIndex: 0,
      borderRadius: 48,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      WebkitOverflowScrolling: 'touch',
      userSelect: 'none',
      background: '#000',
      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
      transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
    },
    notch: true,
  },
  tablet: {
    className: "device-bezel is-ipad flex flex-col items-center justify-center",
    style: {
      width: '1180px',
      height: '820px',
      borderRadius: 30,
      padding: '6px',
      boxSizing: 'border-box',
      boxShadow: '0 25px 50px -15px rgba(0,0,0,0.3), 0 8px 32px rgba(0,0,0,0.15), inset 0 2px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(0,0,0,0.1)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: createBezelGradient(bezelColor),
      border: '1px solid rgba(255,255,255,0.8)',
      aspectRatio: 'auto',
      transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
    },
    screenClass: "device-screen bg-black w-full h-full flex flex-col overflow-hidden rounded-[24px]",
    screenStyle: {
      position: 'relative',
      width: '100%',
      height: '100%',
      zIndex: 0,
      borderRadius: 24,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
    },
    notch: false,
  }
});

export default function DeviceFrame({ forceReconnectAll, onQuit, isEnvironmentOffline, ...props }) {
  const dispatch = useDispatch();
  // State
  const channel = useSelector((state) => state.channel.currentChannel);
  const deviceType = useSelector((state) => state.device.type);
  // Normalize deviceType to known keys to avoid undefined style errors
  const normalizedDeviceType = ['phone', 'tablet'].includes(deviceType) ? deviceType : 'phone';
  const deviceBezelColor = useSelector((state) => state.channel.deviceBezelColor) || '#1a1a2e';
  const channelConfig = useSelector((state) => state.channel.channelConfig);
  const notifications = useSelector((state) => state.notifications.notifications);
  const reduxIsVibrating = useSelector((state) => state.channel.isVibrating);
  const enableSoundEffects = useSelector((state) => state.channel.enableSoundEffects);
  const isCallInProgress = useSelector((state) => state.channel.isCallInProgress);
  const showMobileStatusBar = useSelector((state) => state.channel.showMobileStatusBar);
  const isLightMode = useSelector((state) => state.device.theme) === 'light';
  const iPadDesktopLandscape = useSelector((state) => state.channel.iPadDesktopLandscape);


  const [currentTime, setCurrentTime] = useState(new Date());
  const [appOpenAnimation, setAppOpenAnimation] = useState(null);
  const [homeAnimation, setHomeAnimation] = useState(null);
  const [channelSnapshot, setChannelSnapshot] = useState(null);
  const [frozenChannelState, setFrozenChannelState] = useState(null);
  const [isVibrating, setIsVibrating] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? shouldUseMobileUI() : false);
  // iOS-style status bar follows light mode ONLY in mobile view; in desktop view
  // (the simulated phone bezel) it always stays dark.
  const lightStatusBar = isLightMode && isMobile;
  const statusFg = lightStatusBar ? '#16202e' : '#ffffff';

  // Battery percentage state - represents time until token expiry
  const [batteryPercentage, setBatteryPercentage] = useState(100);
  const [isLowBattery, setIsLowBattery] = useState(false);
  const [isCharging, setIsCharging] = useState(false);
  const [chargingProgress, setChargingProgress] = useState(0);

  // Make isMobile reactive to window resize and orientation changes
  useEffect(() => {
    const handleResize = () => setIsMobile(shouldUseMobileUI());
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Direct sync when the iPad-landscape-desktop trial flag flips. The reducer
  // also dispatches a synthetic resize, but its timing relative to React's
  // commit is unreliable; observing the flag here guarantees DeviceFrame
  // re-renders with the correct isMobile (and therefore correct scale) on the
  // next render, without needing the user to rotate the iPad.
  useEffect(() => {
    setIsMobile(shouldUseMobileUI());
  }, [iPadDesktopLandscape]);

  // Update battery percentage based on token expiry
  useEffect(() => {
    const updateBatteryPercentage = async () => {
      try {
        const tokenInfo = await tokenManager.getTokenInfo();
        
        if (!tokenInfo) {
          setBatteryPercentage(0);
          setIsLowBattery(true);
          return;
        }

        const { timeUntilExpiry } = tokenInfo;
        
        // Assuming token has a typical lifetime of 1 hour (3600000 ms)
        // Calculate percentage based on time remaining
        const typicalTokenLifetime = 60 * 60 * 1000; // 1 hour in ms
        const percentage = Math.max(0, Math.min(100, (timeUntilExpiry / typicalTokenLifetime) * 100));
        
        setBatteryPercentage(Math.round(percentage));
        setIsLowBattery(percentage < 20); // Low battery warning at 20%
        
      } catch (error) {
        console.error('Error updating battery percentage:', error);
      }
    };

    // Update immediately
    updateBatteryPercentage();

    // Update every 30 seconds
    const interval = setInterval(updateBatteryPercentage, 30000);

    return () => clearInterval(interval);
  }, []);

  // Responsive scaling based on viewport size
  const calculateScale = useCallback(() => {
    if (isMobile) return 1;
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get device dimensions
    const deviceWidth = deviceType === 'phone' ? 420 : 1180;
    const deviceHeight = deviceType === 'phone' ? 850 : 820;
    
    // Calculate scale to fit viewport with some padding (100px on each side)
    const maxWidth = viewportWidth - 200;
    const maxHeight = viewportHeight - 200;
    
    const scaleX = maxWidth / deviceWidth;
    const scaleY = maxHeight / deviceHeight;
    
    // Use the smaller scale to ensure it fits, but cap between 0.5 and 0.9
    const calculatedScale = Math.min(scaleX, scaleY);
    return Math.max(0.5, Math.min(0.9, calculatedScale));
  }, [deviceType, isMobile]);

  const [scale, setScale] = useState(calculateScale());

  // Update scale on resize, AND whenever calculateScale itself changes (e.g.
  // when isMobile flips via the iPad-landscape-desktop toggle). Without the
  // immediate recompute, the synthetic `resize` the toggle dispatches fires on
  // a listener that captured the old isMobile, so the phone stays at scale 1
  // until the user actually rotates the device.
  useEffect(() => {
    setScale(calculateScale());
    const handleResize = () => setScale(calculateScale());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateScale]);

  const deviceStyles = useMemo(() => getDeviceStyles(deviceBezelColor), [deviceBezelColor]);
  // Always fall back to a valid style (phone) to prevent "cannot read properties of undefined (reading 'style')"
  const currentDeviceStyle = deviceStyles[normalizedDeviceType] || deviceStyles.phone;
  const showNotch = normalizedDeviceType === 'phone';

  // Resolve the call channel key so we can keep VoiceCallView mounted as a persistent layer
  const callChannelKey = useMemo(
    () => Object.keys(channelConfig).find(k => channelConfig[k]?.type === 'call') || null,
    [channelConfig]
  );

  // Detect low-spec devices for animation optimization
  const isLowSpecDevice = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }
    
    // Check device memory (if available)
    if (navigator.deviceMemory && navigator.deviceMemory < 4) {
      return true;
    }
    
    // Check hardware concurrency (CPU cores)
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
      return true;
    }
    
    return false;
  }, []);

  // Helper function to get channel color
  const getChannelColor = (channelKey) => {
    const config = channelConfig[channelKey];
    if (config && config.color) return config.color;
    
    // Default iOS app colors
    const defaultColors = {
      sms: '#34C759', // Messages - Green
      email: '#007AFF', // Mail - Blue  
      calendar: '#FF3B30', // Calendar - Red
      browser: '#4285F4', // Chrome - Blue
      app: '#0066CC', // Banking - Bank Blue
      call: '#34C759', // Phone - Green
      live: '#007AFF', // TV - Blue
      agent: '#FF6B35' // AI Assistant - Orange
    };
    return defaultColors[channelKey] || '#007AFF';
  };

  const handleHomeButton = () => {
    // If already on home, do nothing
    if (channel === 'home') {
      return;
    }
    
    // Trigger home animation for all devices
    // Capture the current channel content as a snapshot
    setChannelSnapshot(channel);
    
    // Capture current Redux state for the snapshot
    setFrozenChannelState({
      channelConfig,
      currentChannel: channel
    });
    
    // Capture current channel for animation overlay
    setHomeAnimation({
      fromChannel: channel,
      channelColor: getChannelColor(channel),
      timestamp: Date.now()
    });
    
    // Switch to home immediately so it shows behind the animation
    dispatch(setChannel('home'));
    
    // Clear animation after it completes
    const animationDuration = isLowSpecDevice ? 300 : 800;
    setTimeout(() => {
      setHomeAnimation(null);
      setChannelSnapshot(null);
      setFrozenChannelState(null);
    }, animationDuration);
  };

  const handleBatteryClick = async () => {
    // Don't allow multiple charging animations at once
    if (isCharging) return;

    try {
      setIsCharging(true);
      setChargingProgress(0);

      // Start the charging cable "plug in" animation
      const startBattery = batteryPercentage;
      
      // Attempt to refresh the token
      const refreshed = await tokenManager.refreshToken();
      
      if (refreshed) {
        // Success! Animate battery charging to 100%
        const chargeDuration = 3000; // 3 seconds
        const frameRate = 60; // 60fps
        const totalFrames = (chargeDuration / 1000) * frameRate;
        let currentFrame = 0;

        const chargeInterval = setInterval(() => {
          currentFrame++;
          const progress = currentFrame / totalFrames;
          
          // Ease-in-out curve for smooth animation
          const easeProgress = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
          
          // Animate battery from current percentage to 100%
          const newPercentage = startBattery + (100 - startBattery) * easeProgress;
          setBatteryPercentage(Math.round(newPercentage));
          setChargingProgress(easeProgress * 100);
          
          if (currentFrame >= totalFrames) {
            clearInterval(chargeInterval);
            setBatteryPercentage(100);
            setIsLowBattery(false);
            
            // "Unplug" the charging cable after a short delay
            setTimeout(() => {
              setIsCharging(false);
              setChargingProgress(0);
            }, 500);

            // Emit success event for charging sound in App.js
            window.dispatchEvent(new CustomEvent('token-refresh-success', {
              detail: { success: true }
            }));

            // Force WebSocket reconnection with new token
            if (forceReconnectAll) {
              forceReconnectAll();
            }
          }
        }, 1000 / frameRate);

      } else {
        // Token refresh failed - session may have expired
        // Stop charging animation and let token expiry handling take over
        setIsCharging(false);
        setChargingProgress(0);
        
        // If battery is critically low (< 5%), user's session has likely expired
        // The tokenManager will automatically trigger sign-out
        if (batteryPercentage < 5) {
          console.warn('🔋 Session expired - please sign in again');
        } else {
          console.warn('⚠️ Unable to refresh token - will retry automatically');
        }
      }
    } catch (error) {
      console.error('Battery charge (token refresh) failed:', error);
      // Just stop charging animation
      setIsCharging(false);
      setChargingProgress(0);
    }
  };

  // Emit charging state changes to App.js via custom event
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('device-charging-state', {
      detail: { isCharging, chargingProgress }
    }));
  }, [isCharging, chargingProgress]);

  const handleAppOpen = (appInfo) => {
    // Simplified approach: use CSS flexbox centering and pure scale animation
    setAppOpenAnimation({
      ...appInfo,
      // No positioning needed - CSS flexbox handles centering
    });
    
    // Start the animation - switch channel AFTER animation completes
    const animationDuration = isLowSpecDevice ? 200 : 500;
    setTimeout(() => {
      // Switch to the channel after animation finishes
      // Mark as manual navigation for app channel to clear navigation params (load default view)
      if (appInfo.channelKey === 'app') {
        dispatch(setChannel({ channel: appInfo.channelKey, manual: true }));
      } else {
        dispatch(setChannel(appInfo.channelKey));
      }
      // Clear animation state immediately after channel switch
      setAppOpenAnimation(null);
    }, animationDuration);
  };

  // Listen for external home animation triggers (e.g., from WebSocket)
  useEffect(() => {
    const handleExternalHomeRequest = () => {
      // If already on home, do nothing
      if (channel === 'home') {
        return;
      }
      
      // Trigger the same animation sequence as the physical home button
      // Capture the current channel content as a snapshot
      setChannelSnapshot(channel);
      
      // Capture current Redux state for the snapshot
      setFrozenChannelState({
        channelConfig,
        currentChannel: channel
      });
      
      // Get the color for the current channel
      const config = channelConfig[channel];
      const channelColor = (config && config.color) ? config.color : (() => {
        const defaultColors = {
          sms: '#34C759',
          email: '#007AFF',
          calendar: '#FF3B30',
          browser: '#4285F4',
          app: '#0066CC',
          call: '#34C759',
          live: '#007AFF',
          agent: '#FF6B35'
        };
        return defaultColors[channel] || '#007AFF';
      })();
      
      // Capture current channel for animation overlay
      setHomeAnimation({
        fromChannel: channel,
        channelColor: channelColor,
        timestamp: Date.now()
      });
      
      // Switch to home immediately so it shows behind the animation
      dispatch(setChannel('home'));
      
      // Clear animation after it completes
      const animationDuration = isLowSpecDevice ? 300 : 800;
      setTimeout(() => {
        setHomeAnimation(null);
        setChannelSnapshot(null);
        setFrozenChannelState(null);
      }, animationDuration);
    };
    
    window.addEventListener('trigger-home-animation', handleExternalHomeRequest);
    
    return () => {
      window.removeEventListener('trigger-home-animation', handleExternalHomeRequest);
    };
  }, [channel, channelConfig, dispatch, isLowSpecDevice, setChannelSnapshot, setFrozenChannelState, setHomeAnimation]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Use signalStrength from WebSocket hook (0%, 50%, 100%) and convert to signal bars (0-4)
  // 0% -> 0 bars, 50% -> 2 bars, 100% -> 4 bars
  const signalStrength = props.signalStrength === 100 ? 4 : props.signalStrength === 50 ? 2 : 0;

  // Track notifications and trigger vibration animation
  useEffect(() => {
    if (notifications.length > notificationCount) {
      // New notification received - trigger vibration only if online
      if (!isEnvironmentOffline) {
        setIsVibrating(true);
        playVibratingSound(enableSoundEffects, isEnvironmentOffline); // Play vibrating sound if enabled and online
        setTimeout(() => {
          setIsVibrating(false);
          stopVibratingSound(); // Stop sound when vibration ends
        }, 600); // Match animation duration
      } else {
        console.log('[DeviceFrame] Vibration suppressed - phone is offline');
      }
    }
    setNotificationCount(notifications.length);
  }, [notifications.length, notificationCount, enableSoundEffects, isEnvironmentOffline]);

  // Handle Redux vibration state changes (for calls, etc.)
  useEffect(() => {
    if (reduxIsVibrating && !isEnvironmentOffline) {
      setIsVibrating(true);
      playVibratingSound(enableSoundEffects, isEnvironmentOffline); // Play vibrating sound if enabled and online
    } else {
      setIsVibrating(false);
      stopVibratingSound(); // Stop sound when vibration ends
      if (reduxIsVibrating && isEnvironmentOffline) {
        console.log('[DeviceFrame] Vibration suppressed - phone is offline');
      }
    }
  }, [reduxIsVibrating, enableSoundEffects, isEnvironmentOffline]);

  // Debug footer positioning in development
  useEffect(() => {
    if (process.env.REACT_APP_VERBOSE_LOGS === 'true' && isMobile) {
      import('../utils/deviceUtils').then(({ debugFooterPositioning }) => {
        debugFooterPositioning();
      });
    }
  }, [isMobile]);

  // Remove all employee pane logic from DeviceFrame. Only render the device UI here

  return (
    <div 
      style={!isMobile ? {
        position: 'relative',
        width: currentDeviceStyle.style.width,
        height: currentDeviceStyle.style.height,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
        transform: `scale(${scale})`,
        transformOrigin: 'center center',
        transition: 'width 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), height 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
      } : {
        transform: `scale(${scale})`,
        transformOrigin: 'center center',
        transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
      }}>
      <div
        className={!isMobile ? `${currentDeviceStyle.className} ${isVibrating ? 'device-vibrating' : ''}` : (isVibrating ? 'device-vibrating' : '')}
        style={!isMobile ? { ...currentDeviceStyle.style, margin: 0 } : {}}
      >
        {showNotch && !isMobile && (
          <div className="absolute left-1/2 -translate-x-1/2 bg-black z-30"
               style={{ 
                 top: '2px',
                 width: '130px',
                 height: '28px',
                 borderBottomLeftRadius: '16px',
                 borderBottomRightRadius: '16px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
               }}>
            {/* Speaker grill */}
            <div style={{
              position: 'absolute',
              top: '9px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '48px',
              height: '3.5px',
              background: 'rgba(50, 50, 50, 0.7)',
              borderRadius: '2px',
            }}></div>
            {/* Front camera */}
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '50%',
              transform: 'translateX(16px)',
              width: '9px',
              height: '9px',
              background: 'radial-gradient(circle at 30% 30%, rgba(80, 80, 100, 0.5), rgba(20, 20, 30, 0.9))',
              borderRadius: '50%',
              border: '0.5px solid rgba(50, 50, 60, 0.7)',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
            }}></div>
          </div>
        )}

        <div className={!isMobile ? currentDeviceStyle.screenClass : ''} style={
          !isMobile ? currentDeviceStyle.screenStyle :
            { width: '100vw', height: '100vh', background: '#000', position: 'relative', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
        }>
          {/* Screen overlay for realistic effect */}
          {!isMobile && (
            <div className="absolute inset-0 pointer-events-none z-50"
                 style={{
                   background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.03) 25%, transparent 50%, rgba(255,255,255,0.02) 75%, transparent 100%)',
                   borderRadius: 48
                 }}></div>
          )}

          {/* App opening animation overlay - expands from center to fill content area only */}
          {appOpenAnimation && (
            <div className="absolute z-[60] pointer-events-none flex items-center justify-center"
                 style={{
                   top: isMobile ? `calc(${showMobileStatusBar ? '48px' : '0px'} + env(safe-area-inset-top, 0px))` : (showMobileStatusBar ? '48px' : '0px'), // Start after iOS status bar (if shown) + safe area
                   left: '0',
                   right: '0',
                   bottom: isMobile ? 'calc(60px + env(safe-area-inset-bottom, 0px))' : '60px', // End before home button area + safe area
                   borderRadius: '0',
                   overflow: 'hidden',
                   clipPath: 'inset(0 0 0 0)', // Remove rounded clipping for square animation
                   willChange: 'transform, opacity',
                   backfaceVisibility: 'hidden',
                   transform: 'translate3d(0, 0, 0)',
                   WebkitFontSmoothing: 'antialiased',
                   contain: 'strict',
                   isolation: 'isolate',
                   transformStyle: 'preserve-3d'
                 }}>
              <div
                className="app-open-animation app-open-square"
                style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: appOpenAnimation.color,
                  transformOrigin: 'center center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0px',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  transform: 'translate3d(0, 0, 0)',
                  contain: 'strict',
                  isolation: 'isolate',
                  '--translate-x': '0px',
                  '--translate-y': '0px',
                  '--scale': 20, // Scale to fill screen from center
                }}
              >
                {appOpenAnimation.customIconUrl ? (
                  <img 
                    src={appOpenAnimation.customIconUrl} 
                    alt={appOpenAnimation.title}
                    style={{ 
                      width: '32px',
                      height: '32px',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      animation: 'appIconScale 0.5s ease-out forwards',
                      transformOrigin: 'center center',
                      willChange: 'transform, opacity',
                      imageRendering: 'pixelated',
                      transform: 'translate3d(0, 0, 0)',
                    }}
                  />
                ) : (
                  <span 
                    className="material-icons text-white" 
                    style={{ 
                      fontSize: '28px', 
                      animation: 'appIconScale 0.5s ease-out forwards',
                      transformOrigin: 'center center',
                      display: 'inline-block',
                      willChange: 'transform, opacity',
                      transform: 'translate3d(0, 0, 0)'
                    }}
                  >
                    {appOpenAnimation.icon}
                  </span>
                )}
              </div>
            </div>
          )}
          

          
          {showMobileStatusBar && (
          <div className="status-bar h-12 flex items-center justify-between px-6 pt-3 text-sm font-medium z-20 relative"
                                style={{
                                  minHeight: '48px',
                                  color: statusFg,
                                  background: lightStatusBar ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.1)',
                                  backdropFilter: 'blur(20px)',
                                  WebkitBackdropFilter: 'blur(20px)',
                                  position: isMobile ? 'fixed' : 'relative',
                                  top: isMobile ? '0' : 'auto',
                                  left: isMobile ? '0' : 'auto',
                                  width: isMobile ? '100vw' : 'auto',
                                  paddingTop: isMobile ? 'calc(12px + env(safe-area-inset-top, 0px))' : '12px',
                                }}>
            <div className="flex items-center space-x-3">
              <span className="font-semibold">
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
              </span>
              {/* Call indicator - shows when call is active, positioned after time */}
              {isCallInProgress && (
                <button
                  onClick={() => dispatch(setChannel('call'))}
                  className="flex items-center space-x-1.5 bg-green-500/20 px-2.5 py-1 rounded-full border border-green-500/30 hover:bg-green-500/30 transition-colors cursor-pointer"
                  title="Return to active call"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                  <span className="text-xs font-semibold text-green-300">Call</span>
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={forceReconnectAll}
                className="flex items-end space-x-0.5 p-1 rounded hover:bg-white/10 transition-colors"
                title="Tap to force WebSocket reconnection"
              >
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="w-1 rounded-sm"
                    style={{ height: `${(index + 1) * 3 + 2}px`, backgroundColor: index < signalStrength ? statusFg : (lightStatusBar ? 'rgba(22,32,46,0.3)' : '#6b7280') }}
                  ></div>
                ))}
              </button>
              <button
                onClick={handleBatteryClick}
                className="flex items-center space-x-1 p-1 rounded hover:bg-white/10 transition-colors"
                title={isCharging ? "Charging..." : "Tap to refresh token"}
              >
                <span className="text-xs">{Math.round(batteryPercentage)}%</span>
                <div className="w-6 h-3 border rounded-sm flex items-center justify-end pr-0.5 relative overflow-hidden" style={{ borderColor: statusFg }}>
                  {/* Battery fill */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 rounded-sm transition-all duration-100"
                    style={{ 
                      width: `${batteryPercentage}%`,
                      backgroundColor: isCharging ? '#FFD700' : (isLowBattery ? '#FF3B30' : '#34C759')
                    }}
                  ></div>
                  {/* Charging bolt icon */}
                  {isCharging && (
                    <svg 
                      className="absolute inset-0 w-full h-full z-10 animate-pulse" 
                      viewBox="0 0 24 24" 
                      fill="white"
                      style={{ filter: 'drop-shadow(0 0 2px rgba(255,215,0,0.8))' }}
                    >
                      <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
                    </svg>
                  )}
                </div>
              </button>
            </div>
          </div>
          )}
          {/* In-device notifications */}
          <NotificationContainer />
          {/* Main content area — call channel is excluded here; it lives in the persistent layer below */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{
              paddingTop: isMobile ? (showMobileStatusBar ? '48px' : 0) : 0,
              minHeight: 0
            }}>
            {channel === 'home'
              ? <HomeView onAppOpen={handleAppOpen} />
              : (channel !== callChannelKey
                  ? <ChannelContent isEnvironmentOffline={isEnvironmentOffline} />
                  : null)}
          </div>

          {/* Persistent call layer — keeps LiveKit alive when navigating away during a call.
              Rendered whenever the call channel is active OR a call is in progress.
              Hidden (display:none) when not on the call channel so it stays mounted but invisible. */}
          {callChannelKey && (channel === callChannelKey || isCallInProgress) && (
            <div
              style={{
                display: channel === callChannelKey ? 'flex' : 'none',
                position: 'absolute',
                top: isMobile ? `calc(${showMobileStatusBar ? '48px' : '0px'} + env(safe-area-inset-top, 0px))` : (showMobileStatusBar ? '48px' : '0px'),
                left: 0,
                right: 0,
                bottom: isMobile ? 'calc(60px + env(safe-area-inset-bottom, 0px))' : '60px',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 2,
              }}
            >
              <VoiceCallView isEnvironmentOffline={isEnvironmentOffline} />
            </div>
          )}


          
          {/* Home animation overlay - shows the previous channel content shrinking over the home view */}
          {homeAnimation && channelSnapshot && (
            <div className="absolute z-[65] pointer-events-none"
                 style={{
                   top: isMobile ? `calc(${showMobileStatusBar ? '48px' : '0px'} + env(safe-area-inset-top, 0px))` : (showMobileStatusBar ? '48px' : '0px'), // Start after iOS status bar (if shown) + safe area
                   left: '0',
                   right: '0',
                   bottom: isMobile ? 'calc(60px + env(safe-area-inset-bottom, 0px))' : '60px', // End before home button area + safe area
                   borderRadius: isMobile ? '0' : '0 0 48px 48px',
                   overflow: 'hidden',
                   clipPath: isMobile ? 'inset(0 0 0 0)' : 'inset(0 0 0 0 round 0 0 48px 48px)',
                 }}>
              <div
                className="home-animation"
                style={{
                  width: '100%',
                  height: '100%',
                  background: '#fff',
                  willChange: 'transform, opacity',
                  backfaceVisibility: 'hidden',
                  transform: 'translate3d(0, 0, 0)',
                  WebkitFontSmoothing: 'antialiased',
                  transformOrigin: 'center center',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
                  borderRadius: '24px', // Add more pronounced rounded corners for app closing
                  overflow: 'hidden'
                }}
              >
                {/* Render the actual previous channel content */}
                <ChannelSnapshot channelKey={channelSnapshot} frozenState={frozenChannelState} />
              </div>
            </div>
          )}

          {/* Footer bar - For mobile: just spacing. For desktop/tablet: home button */}
          {isMobile ? (
            // Mobile: Just spacing for FooterCarousel
            <div style={{
              height: '80px',
              flexShrink: 0,
              background: 'transparent'
            }} />
          ) : (
            // Desktop/Tablet: Home button footer
            <div className="relative bg-black flex items-center justify-center"
                 style={{
                   borderRadius: '0 0 48px 48px',
                   background: '#000',
                   height: '60px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}>
              <button
                onClick={handleHomeButton}
                className="rounded-full transition-transform duration-100 active:scale-90 flex items-center justify-center home-button w-10 h-10"
                style={{
                  background: createHomeButtonGradient(deviceBezelColor),
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.5)',
                  border: '1px solid rgba(0,0,0,0.3)'
                }}
                title="Home"
              >
                <span 
                  className="material-icons text-lg"
                  style={{ 
                    color: '#e5e7eb',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                  }}
                >
                  home
                </span>
              </button>
            </div>
          )}

          {/* Environment Offline Screen - iPhone flat battery style - Rendered inside device screen */}
          {isEnvironmentOffline && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#000000',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                borderRadius: isMobile ? '0' : '48px',
                overflow: 'hidden',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            >
              {/* Battery Icon - Similar to iPhone low battery screen */}
              <div
                style={{
                  width: '120px',
                  height: '56px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '4px',
                  marginBottom: '24px',
                }}
              >
                {/* Battery terminal */}
                <div
                  style={{
                    position: 'absolute',
                    right: '-8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '6px',
                    height: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '0 2px 2px 0',
                  }}
                />
                {/* Empty battery - no fill to indicate completely dead/unavailable */}
              </div>
              
              {/* Offline Message */}
              <div
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '15px',
                  fontWeight: '400',
                  textAlign: 'center',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  lineHeight: '1.5',
                  maxWidth: '280px',
                  padding: '0 20px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                }}
              >
                This demo is currently unavailable. Please try again later.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
